/** @jsx jsx */
/** @jsxFrag null */
import isEqual from 'lodash/isEqual';
import {produce} from 'immer';
import {VNode} from 'snabbdom';
import {jsx} from 'panel';

import {SelectEl} from 'common/components/select';
import {
  ChartType,
  FeatureGate,
  ProjectFeatureFlag,
  ReportName,
  SavedLegend,
  ValueRepresentationType,
} from 'common/report/constants';
import {ReportVisualization} from 'common/report/report-manager/visualization';
import {isPeopleTimeSeries} from 'common/report/insights/util';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {getDateRange} from 'common/report/insights/util/time';
import {
  AnalysisType,
  AnalysisTypeLabelKey,
  DisplayOptions,
  InsightsBookmarkParams,
  InsightsQueryError,
  InsightsQueryResults,
} from 'common/report/insights/util/types';
import {getOwnAttr} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {OrderedStandardTimeUnits, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {ChartHeaderAttrs, ChartHeaderParams} from 'common/widgets/chart-header/types';
import {propagateEvent} from 'common/util/dom';
import {SegmentFilterDataTableJsxProps} from 'common/charts/misc/segment-filter-data-table';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {ColumnResizeEvent, SortChangeEvent} from 'common/components/data-table/types';
import {DataSortMenuEl} from 'common/widgets/data-sort-menu';
import {ReportStatus} from 'common/widgets/warning-hero/constants';
import {canUseViewN} from 'common/components/data-table/utils';
import {SortBy, SortConfig} from 'common/report/util/sorting/types';
import {VIEW_N_DEFAULT_LIMIT} from 'common/components/data-table/constants';
import {SelectTheme} from 'common/components/select/constants';
import {TableToggleableMetrics} from 'common/report/funnels/models/constants';
import {
  TABLE_COLUMN_SELECTOR_DISPLAY_NAME,
  TABLE_COLUMN_SELECTOR_TOOLTIPS,
} from 'common/report/funnels/models/constants';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {getFormattedRowData, hasRetentionData} from 'common/report/retention/util/parsing';
import {RetentionBookmarkType} from 'common/report/insights/charts/retention-curve-chart/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {MultimetricBookmarkController} from 'common/report/retention/bookmark-controller/multimetric';
import {RequestOdaDetail} from 'common/report/report-manager/types';
import {ODAControlEl} from 'common/widgets/oda-control';
import {Retention} from 'common/report/retention/models/types';

import {Report} from '../models/report';
import {
  hasEventBreakdown,
  hasEventComparison,
  hasFunnelConversionMetric,
  hasGroupByCohort,
  hasRetentionFrequency,
  hasRetentionRateMetric,
  isRetentionOnlyInsightsBookmark,
} from '../models/utils';
import {InsightsRetentionTableEl} from '../charts/insights-retention-table-chart';
import {TimeClause} from '../models/time-clause';
import {InsightsChartDisplayEl} from './charts/chart-display';
import {
  bookmarkUpdatesFromAnalysisChange,
  bookmarkUpdatesFromHeaderUpdate,
  correctDisplayOptions,
  getAnalysisMenuItems,
  getChartTypeSectionOverrides,
  getLeafLevelSegments,
  getRollingOptions,
  getSelectedAnalysisIcon,
  getSelectedAnalysisKey,
  getSelectedChartType,
  getSelectedRollingOption,
  getTimeComparisonDisabledReason,
  getTimeComparisonDisabledReasonMessage,
  getTimeUnitSectionOverrides,
  shouldRequeryDisplayOptions,
} from './util';
import {getDataTableTrackName} from './charts/chart-display/utils';

export class InsightsVisualization extends ReportVisualization<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryError
> {
  get reportName() {
    if (this.attr(`insights-report-mode`) === BookmarkType.Funnels) {
      return ReportName.Funnels;
    }
    if (this.attr(`insights-report-mode`) === BookmarkType.Retention) {
      return ReportName.Retention;
    }
    return ReportName.Insights;
  }

  get defaultState() {
    return {
      // TODO remove this once table column widths are persisted to bookmark
      tableChartColumnWidthOverrides: {},
    };
  }

  hasChartCustomization() {
    const currentProject = this.getContext(`currentProject`).get()?.value;
    return currentProject?.hasFeatureFlag(ProjectFeatureFlag.ChartCustomization);
  }

  chartDisplay() {
    return (
      <InsightsChartDisplayEl
        attrs={{
          'resizing-layout': this.attr(`show-resizing-layout`),
          'rca-source-id': this.attr(`rca-source-id`),
          elref: `chartDisplay`,
          'can-use-view-n': canUseViewN(),
        }}
        props={{
          bookmarkParams: this.bookmarkParams,
          queryResults: this.results,
          queryError: null,
          dataGroups: this.dataGroups,
          tableChartColumnWidthOverrides: this.state.tableChartColumnWidthOverrides,
        }}
        on={{
          'export-csv': (ev) => propagateEvent(this, ev),
          update: ({detail}) => {
            let requery = true;

            // chart updates will not have changes alongside sorting or columnWidths,
            // so safe to assume we don't have to query when present.
            const {sorting, columnWidths} = detail.delta;
            if (sorting || columnWidths) {
              requery = false;
            }

            this.dispatchUpdate({
              params: {...this.bookmarkParams, ...detail.delta},
              requery,
            });
          },
          columnResize: (ev) => {
            this.handleDataTableColumnResize(ev);
          },
        }}
      />
    );
  }

  // override data table template for custom retention table
  dataTableTemplate(): VNode {
    const chartType = this.bookmarkParams.displayOptions.chartType;
    if (isRetentionOnlyInsightsBookmark(this.bookmarkParams) && chartType === ChartType.RetentionCurve) {
      const queryResults = this.results as unknown as InsightsQueryResults<Retention>;
      const bookmarkController = new MultimetricBookmarkController(this.bookmarkParams, queryResults);
      const incompleteBuckets = queryResults.meta.report_sections.show[0].incomplete_buckets;
      const segments = bookmarkController.getSegments();
      const {data} = bookmarkController.flattenData({queryResults, segments});
      const tableData = getFormattedRowData({
        data,
        incompleteBuckets,
        segmentGroups: segments,
        timezone: this.project?.timezone,
        metricKeyLookup: bookmarkController.metricKeyLookup,
      });
      const rowLimit = this.attr(`is-download-mode`) ? 30 : 255;
      const rowData = tableData.slice(0, rowLimit);
      const tableHasData = hasRetentionData(tableData);
      if (!tableHasData) {
        return null;
      }
      return (
        <InsightsRetentionTableEl
          sel='.retention-curve-table'
          params={{
            rowData,
            bookmarkData: {
              bookmark: this.bookmarkParams,
              queryResults,
              type: RetentionBookmarkType.MULTIMETRIC,
            },
            projectId: this.attr(`project-id`),
            projectTimezone: this.project?.timezone,
            resizingLayout: this.attr(`show-resizing-layout`),
            onSortChange: (sorting: SortConfig) => {
              this.dispatchUpdate({
                params: {
                  ...this.bookmarkParams,
                  sorting: {
                    ...this.bookmarkParams.sorting,
                    [ChartType.RetentionCurve]: sorting,
                  },
                },
                requery: false,
              });
            },
            onExpandedSegmentsChange: (expandedSegments: string[]) =>
              this.handleExpandedSegmentsChange(expandedSegments),
          }}
        />
      );
    }

    return super.dataTableTemplate();
  }

  dataTableFooterTemplate() {
    if (this.attr(`is-download-mode`)) {
      return null;
    }
    if (this.attr(`insights-report-mode`) === BookmarkType.Insights) {
      return null;
    }
    if (this.attr(`insights-report-mode`) === BookmarkType.Funnels) {
      if (hasEventComparison(this.bookmarkParams.sections.show)) {
        return null;
      }
      if (hasEventBreakdown(this.bookmarkParams.sections.group)) {
        return null;
      }
      if (!hasFunnelConversionMetric(this.bookmarkParams)) {
        return null;
      }
    }
    if (this.attr(`insights-report-mode`) === BookmarkType.Retention) {
      if (hasGroupByCohort(this.bookmarkParams.sections.group)) {
        return null;
      }
      if (hasRetentionFrequency(this.bookmarkParams.sections.show)) {
        return null;
      }
      if (!hasRetentionRateMetric(this.bookmarkParams)) {
        return null;
      }
    }

    return (
      <ODAControlEl
        sel='.oda-control'
        attrs={{
          elref: `odaControl`,
          description:
            this.attr(`insights-report-mode`) === BookmarkType.Retention
              ? `Receive an email of statistically significant segments impacting retention.`
              : `Receive an email of statistically significant segments impacting this funnel.`,
          'show-upsell': !this.project?.getFeatureGate(FeatureGate.OnDemandAnalysis),
          'upsell-description':
            this.attr(`insights-report-mode`) === BookmarkType.Retention
              ? `Rather than searching through multiple property and cohort breakdowns to find significant data, receive an email of statistically significant segments with higher or lower retention.`
              : `Rather than searching through multiple property and cohort breakdowns to find significant data, receive an email of statistically significant segments with higher or lower conversion for any funnel you create.`,
        }}
        on={{
          clickAnalyze: () => {
            const bookmarkDateRange = getDateRange(
              this.bookmarkParams.sections.time.map((time) => new TimeClause(time)),
            );
            const detail: RequestOdaDetail = {
              bookmarkDateRange: bookmarkDateRange.getBookmarkFormat(),
              bookmarkUrl: window.location.hash.slice(1),
            };
            this.dispatchEvent(new CustomEvent<RequestOdaDetail>(`request-oda`, {detail}));
          },
        }}
      />
    );
  }

  reportSpecificDataTableAttrs(): SegmentFilterDataTableJsxProps['attrs'] {
    const trackName = getDataTableTrackName(this.bookmarkParams.displayOptions.chartType);
    return {'track-name': trackName};
  }

  reportSpecificResizingLayoutAttrs() {
    if (this.bookmarkParams?.displayOptions?.chartType === ChartType.Table) {
      return {'disable-content-padding': true};
    }
    return {};
  }

  reportSpecificChartExtras() {
    if (this.bookmarkParams?.displayOptions?.chartType === ChartType.FunnelSteps) {
      const items = Object.values(TableToggleableMetrics).map((column: TableToggleableMetrics) => ({
        isSelected: this.bookmarkParams.displayOptions.funnelStepsSelectedTableColumns?.[column],
        label: this.print(TABLE_COLUMN_SELECTOR_DISPLAY_NAME[column]),
        tooltip: {
          text: this.print(TABLE_COLUMN_SELECTOR_TOOLTIPS[column]),
          placement: Placement.Left,
          size: DisplaySize.Small,
        },
        value: column,
        ...(column === TableToggleableMetrics.StatSig
          ? {isDisabled: this.bookmarkParams.sections.group.length === 0} // TODO event comparisons?
          : {}),
      }));
      return (
        <SelectEl
          attrs={{
            elref: `funnelStepMetricSelector`,
            icon: {leftIcon: `settings`},
            'icon-only': true,
            theme: SelectTheme.Secondary,
            multiple: true,
            'menu-width': 270,
            tooltip: {text: this.t(`reports.select-table-column.selector-tooltip`)},
          }}
          props={{
            sections: [{items}],
          }}
          on={{
            change: (ev) => this.updateFunnelStepColumns(ev),
          }}
        />
      );
    }
    return null;
  }
  updateFunnelStepColumns(ev) {
    const column = ev.detail.item.value;
    const defaultTableColumns = Object.fromEntries(Object.values(TableToggleableMetrics).map((key) => [key, false])); // TODO set this up when selecting the chart
    const tableColumns = this.bookmarkParams.displayOptions.funnelStepsSelectedTableColumns || defaultTableColumns;
    const funnelStepsSelectedTableColumns = {
      ...tableColumns,
      [column]: !tableColumns[column],
    } as DisplayOptions[`funnelStepsSelectedTableColumns`];
    this.updateDisplayOptions({...this.bookmarkParams.displayOptions, funnelStepsSelectedTableColumns});
  }

  getBookmarkParamsFromSortChange(ev: SortChangeEvent): InsightsBookmarkParams {
    let {chartType} = this.bookmarkParams.displayOptions;
    if (chartType === ChartType.FunnelSteps) {
      chartType = ChartType.Bar;
    }
    let sortConfig = ev.detail.sorting;
    if (
      canUseViewN() &&
      chartType === ChartType.Table &&
      this.bookmarkParams.sorting[chartType].sortBy !== sortConfig.sortBy
    ) {
      // Use default viewNLimits when SortBy changes
      sortConfig = produce(sortConfig, (draft) => {
        if (draft.sortBy === SortBy.Value) {
          draft.viewNLimit = VIEW_N_DEFAULT_LIMIT;
        } else {
          draft.colSortAttrs.forEach((colSortAttr) => {
            colSortAttr.viewNLimit = VIEW_N_DEFAULT_LIMIT;
          });
        }
      });
    }

    const chartTypeForSorting = chartType === ChartType.Column ? ChartType.Line : chartType;
    return {
      ...this.bookmarkParams,
      sorting: {
        ...this.bookmarkParams.sorting,
        [chartTypeForSorting]: sortConfig,
      },
    };
  }

  getMultimetricChartTypes() {
    if (this.reportName === ReportName.Funnels) {
      return [ChartType.FunnelSteps, ChartType.Line, ChartType.Bar, ChartType.Metric, ChartType.FunnelTopPaths];
    }
    if (this.reportName === ReportName.Retention) {
      return [ChartType.RetentionCurve, ChartType.Line, ChartType.Metric];
    }
    // default for insights
    return [
      ChartType.Line,
      ChartType.StackedLine,
      ChartType.Bar,
      ChartType.StackedBar,
      ChartType.Pie,
      ChartType.Table,
      ChartType.Metric,
      ChartType.Column,
      ChartType.StackedColumn,
      ChartType.FunnelSteps,
      ChartType.RetentionCurve,
    ];
  }

  reportSpecificHeaderAttrs(): Partial<ChartHeaderAttrs> {
    // TODO calculate attrs directly from bookmark params
    // for now, use the Report model for parity with Insights app
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const timeComparisonDisabledReason = getTimeComparisonDisabledReason(report); // TODO overrides
    const breakdowns = getSelectedSegments(report.sections.group.clauses, false);

    // TODO hopefully some of these can be shared among different report types
    return {
      breakdowns,
      'should-divide-chart-types': this.reportName === ReportName.Insights,
      'chart-types': this.getMultimetricChartTypes(),
      'comparison-date-range': report.timeComparison,
      'date-range': getDateRange(report.sections.time.clauses).getAttributes(),
      'date-units':
        report.showTimeUnitAggregation() && OrderedStandardTimeUnits.filter((value) => value !== TimeUnit.Year),
      'selected-chart-type': getSelectedChartType(report),
      'selected-date-unit': report.timeUnit(),
      'show-date-range-selector':
        !report.sections.show.isPeopleOnlyQuery() || !isPeopleTimeSeries(this.bookmarkParams.sections),
      'time-comparison-disabled-reason': getTimeComparisonDisabledReasonMessage(report, timeComparisonDisabledReason),

      // lift-specific attrs
      'enable-lift-comparison': true,
      'selected-breakdown-segments': getLeafLevelSegments({
        breakdowns,
        queryResults: this.results,
        report,
      }),
      'selected-value-representation':
        (report.displayOptions && report.displayOptions.value) || ValueRepresentationType.Absolute,
      'value-representations': [],
      'selected-lift-comparison': report.liftComparison,
      'lift-type-overrides': report.getLiftTypeOverrides(),
    };
  }

  reportSpecificHeaderParams(): Partial<ChartHeaderParams> {
    const report = Report.fromBookmarkData(this.bookmarkParams);
    return {
      bookmarkParams: this.bookmarkParams,
      chartTypeOverrides: getChartTypeSectionOverrides({
        bookmarkParams: Report.fromBookmarkData(this.bookmarkParams).toBookmarkData(),
        metricsResource: this.getContext(`metrics`),
        behaviorsResource: this.getContext(`behaviors`),
      }),
      dateUnitOverrides: getTimeUnitSectionOverrides(report),
    };
  }

  protected headerChildrenTemplate() {
    // Analysis type menu (linear, rolling, etc)
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const sections = getAnalysisMenuItems({report});
    if (sections) {
      sections[0].items = sections[0].items.map((item) => ({
        ...item,
        label: this.t(AnalysisTypeLabelKey[item.value]),
        tooltip: item.tooltip && {
          ...item.tooltip,
          text: this.print(item.tooltip.text),
        },
      }));
    }

    const sortControls = report.displayOptions.chartType === ChartType.Table &&
      (Boolean(this.attr(`status`)) === false || this.attr(`status`) === ReportStatus.Loading) && (
        <>
          <DataSortMenuEl
            attrs={{
              slot: `report-controls`,
            }}
            params={{
              isLoading: this.attr(`status`) === ReportStatus.Loading,
              sortConfig: this.getContext(`segmentFilterController`)?.tableSortConfig,
              defaultSortConfig: this.getContext(`segmentFilterController`)?.defaultSortConfig,
              columns: this.getContext(`segmentFilterController`)?.getSections()[0]?.tableColumns,
              chartType: this.bookmarkParams.displayOptions.chartType,

              onSortChange: (ev: SortChangeEvent) => {
                this.dispatchUpdate({
                  params: this.getBookmarkParamsFromSortChange(ev),
                  requery: false,
                });
              },
            }}
          />
        </>
      );

    const analysisControls = sections && (
      <>
        {report.getDisplayAnalysis() === AnalysisType.Rolling && (
          <div sel='.chart-control-extra'>
            <SelectEl
              attrs={{
                'selected-option': getSelectedRollingOption(report),
                sections: getRollingOptions(report),
              }}
              on={{
                change: (ev) => this.handleRollingWindowChange(ev.detail.item.value),
              }}
            />
          </div>
        )}
        {!this.hasChartCustomization() && (
          <div sel='.chart-control'>
            <SelectEl
              attrs={{
                'selected-option': this.t(getSelectedAnalysisKey(report)),
                icon: {leftIcon: getSelectedAnalysisIcon(report)},
                'menu-width': 160,
                sections,
              }}
              on={{
                change: (ev) => this.handleAnalysisSelectChange(ev.detail.item.value),
              }}
            />
          </div>
        )}
      </>
    );
    const extraControls = this.reportSpecificChartExtras();

    if (analysisControls || sortControls || extraControls) {
      return (
        <>
          {sortControls}
          <div sel='.chart-controls-extra' attrs={{slot: `chart-controls`}}>
            {analysisControls}
            {extraControls}
          </div>
        </>
      );
    } else {
      return null;
    }
  }

  updateDisplayOptions(options: DisplayOptions) {
    const oldDisplayOptions = this.bookmarkParams.displayOptions;
    const newDisplayOptions = correctDisplayOptions(options);

    if (!isEqual(oldDisplayOptions, newDisplayOptions)) {
      const requery = shouldRequeryDisplayOptions(oldDisplayOptions, newDisplayOptions);
      this.dispatchUpdate({
        params: {...this.bookmarkParams, displayOptions: newDisplayOptions},
        requery,
      });
    }
  }

  handleExpandedSegmentsChange(expandedSegments: string[]) {
    const savedLegend: SavedLegend = {
      ...this.bookmarkParams.legend?.individualSelectedSegments,
      expanded: expandedSegments.map((key) => ({key})),
    };
    this.dispatchUpdate({
      params: {
        ...this.bookmarkParams,
        legend: {
          ...this.bookmarkParams.legend,
          individualSelectedSegments: savedLegend,
        },
      },
      requery: false,
    });
  }

  handleRollingWindowChange(rollingWindowSize) {
    this.updateDisplayOptions({
      ...this.bookmarkParams.displayOptions,
      rollingWindowSize,
    });
  }

  handleAnalysisSelectChange(analysis: AnalysisType) {
    const {displayOptions} = bookmarkUpdatesFromAnalysisChange(this.bookmarkParams, analysis);
    this.updateDisplayOptions(displayOptions);
  }

  handleDataTableColumnResize(ev: ColumnResizeEvent) {
    this.update({tableChartColumnWidthOverrides: ev.detail.columnWidthOverrides});
  }

  bookmarkUpdateFromHeaderUpdate(update: ChartHeaderUpdateDetail) {
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const bookmarkUpdates = bookmarkUpdatesFromHeaderUpdate({
      report,
      update,
      hasChangedTimeUnit: this.state.hasChangedTimeUnit,
      behaviorsResource: this.getContext(`behaviors`),
    });

    if (getOwnAttr(update.delta, `dateUnit`)) {
      this.update({hasChangedTimeUnit: true});
    }

    let requery = true;
    let hasChangedChartType = false;
    if (bookmarkUpdates.displayOptions) {
      requery = shouldRequeryDisplayOptions(this.bookmarkParams.displayOptions, bookmarkUpdates.displayOptions);
      const {chartType, plotStyle} = this.bookmarkParams.displayOptions;
      hasChangedChartType =
        chartType !== bookmarkUpdates.displayOptions.chartType ||
        plotStyle !== bookmarkUpdates.displayOptions.plotStyle;
    }

    const params = {...this.bookmarkParams, ...bookmarkUpdates};
    return {params, queryReason: QueryReason.QbOtherUpdate, requery, hasChangedChartType};
  }
}

export const InsightsVisualizationEl = defineAndMakeJsxFactory(`insights-visualization`, InsightsVisualization);
export const InsightsVisualizationReact = panel2react(`insights-visualization`, InsightsVisualization);
